import type { QueryClient } from "@tanstack/react-query";
import type { TaskListQuery } from "@common/contracts/task.contract";
import { organizationTaskQueryKeys } from "./organizationTask.api";

export const tasksQueryKeys = {
    all: () => ["tasks"] as const,
    list: (workspaceKey: string, query?: TaskListQuery) =>
        [...tasksQueryKeys.all(), workspaceKey, "list", query] as const,
    listByWorkspaceKey: (workspaceKey: string) =>
        [...tasksQueryKeys.all(), workspaceKey, "list"] as const,
    detailByWorkspaceKey: (workspaceKey: string) =>
        [...tasksQueryKeys.all(), workspaceKey, "detail"] as const,
    detail: (workspaceKey: string, taskId: string) =>
        [...tasksQueryKeys.all(), workspaceKey, "detail", taskId] as const,
    timeInStatus: (workspaceKey: string, taskId: string) =>
        [
            ...tasksQueryKeys.all(),
            workspaceKey,
            "time-in-status",
            taskId,
        ] as const,
    summaries: (workspaceKey: string) =>
        [...tasksQueryKeys.all(), workspaceKey, "summaries"] as const,
    threadUnreadCounts: (
        workspaceKey: string,
        query?: TaskListQuery,
        organizationId?: string | null,
    ) =>
        [
            ...tasksQueryKeys.all(),
            workspaceKey,
            "thread-unread-counts",
            query,
            organizationId ?? "",
        ] as const,
    threadUnreadCountsByWorkspaceKey: (workspaceKey: string) =>
        [
            ...tasksQueryKeys.all(),
            workspaceKey,
            "thread-unread-counts",
        ] as const,
};

export const todoTasksCountQueryKey = (activeWorkspaceKey: string) =>
    ["tasks", "todo", activeWorkspaceKey] as const;

/** Refetches the workspace To do count and every org-level task read, such as the org Tasks badge. */
export const invalidateTodoTaskCounts = (
    queryClient: QueryClient,
    activeWorkspaceKey: string,
) =>
    Promise.all([
        queryClient.invalidateQueries({
            queryKey: todoTasksCountQueryKey(activeWorkspaceKey),
            exact: true,
        }),
        queryClient.invalidateQueries({
            queryKey: organizationTaskQueryKeys.all(),
        }),
    ]);

/** Refetches every task read: all workspace task queries and the organization Tasks page's lists and unread counts. */
export const invalidateAllTaskQueries = (queryClient: QueryClient) =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: tasksQueryKeys.all() }),
        queryClient.invalidateQueries({
            queryKey: organizationTaskQueryKeys.all(),
        }),
    ]);

/** Refetches a workspace's task lists, task details, summaries and To do counts. */
export const invalidateWorkspaceTaskQueries = (
    queryClient: QueryClient,
    workspaceKey: string,
) =>
    Promise.all([
        queryClient.invalidateQueries({
            queryKey: tasksQueryKeys.listByWorkspaceKey(workspaceKey),
            exact: false,
        }),
        queryClient.invalidateQueries({
            queryKey: tasksQueryKeys.detailByWorkspaceKey(workspaceKey),
            exact: false,
        }),
        queryClient.invalidateQueries({
            queryKey: tasksQueryKeys.summaries(workspaceKey),
            exact: true,
        }),
        invalidateTodoTaskCounts(queryClient, workspaceKey),
    ]);
